import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { TrashCan } from '../../components/Icons';
import Table from '../../components/Table';
import { domain } from '../../d-man';
import { Trade, useCreateTrade, useGetTrades } from '../../queries/Trades';
import Pager from '../../components/Pager';
import { useCurrentProjectId } from '../../queries/Accounts';
import { useGetPermissions } from '../../queries/Permissions';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function Trades(props: RouteComponentProps) {
    const PAGE_SIZE = 30;
    const [skip, setSkip] = useState(0);
    const trades = useGetTrades(skip, PAGE_SIZE);

    const newTrade = useCreateTrade();

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [description, setDescription] = useState('');
    const [code, setCode] = useState('');

    const projectId = useCurrentProjectId();
    const getPermissions = useGetPermissions('PROJECT', projectId.data);
    const { navigate } = props;
    // REDIRECT IF NOT CORRECT ROLE
    if (
        getPermissions.data &&
        !getPermissions.data?.items.find((p) => p.code === 'Project_Allow_Admin')?.hasAccess &&
        navigate
    ) {
        navigate(`/`);
    }

    useEffect(() => {
        if (newTrade.error) {
            setErrorMessage(newTrade.error || 'Failed to create new Trade');
        }
    }, [newTrade.error]);

    const toDetail = (id?: string) => {
        if (props.navigate && id) {
            props.navigate(`/trades/${id}`);
        }
    };

    const post = async () => {
        setIsLoading(true);
        setErrorMessage('');
        if (!description || !code) {
            setErrorMessage('Please capture all the fields above');
        } else {
            const data = {
                description,
                code,
            };
            await newTrade.execute(data);
            trades.reset();
        }
        setIsLoading(false);
    };

    const clearFields = () => {
        setErrorMessage('');
        setDescription('');
        setCode('');
    };

    /**
     * Does an HTTP Delete to the server for the specified id
     * @param id
     */
    const deleteTrade = async (id?: string) => {
        const remove = domain.delete(`/trades/${id}`);
        await remove.execute();
        trades.reset();
    };

    const handleNextPage = () => {
        setSkip(skip + PAGE_SIZE);
        trades.reset();
    };

    const handlePreviousPage = () => {
        setSkip(Math.max(skip - PAGE_SIZE, 0));
        trades.reset();
    };

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-wrap items-end justify-between gap-4 bg-surface border border-border rounded-xl m-4 p-4">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="text-sm font-semibold text-foreground pb-2 pr-4 border-r border-border">
                        New Trade
                    </div>
                    <div className="w-48">
                        <Input
                            label="Code"
                            value={code}
                            type="text"
                            onChange={(event) => setCode(event.target.value)}
                        />
                    </div>
                    <div className="w-48">
                        <Input
                            label="Description"
                            value={description}
                            type="text"
                            onChange={(event) => setDescription(event.target.value)}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {errorMessage && <div className="text-sm text-danger">{errorMessage}</div>}
                    <Button secondary onClick={clearFields}>
                        Clear
                    </Button>
                    <Button onClick={post}>{!isLoading ? 'Submit' : <div>Loading</div>}</Button>
                </div>
            </div>
            <div className="m-4">
                <Table<Trade>
                    loading={trades.loading && 'Fetching Trades from server'}
                    error={
                        trades.error &&
                        `Failed to fetch Trades list from the server (${trades.error})`
                    }
                    data={trades.data?.items}
                    columns={[
                        {
                            key: 'code',
                            title: 'Trade Code',
                            onClick: (trade) => toDetail(trade.id),
                        },
                        {
                            key: 'description',
                            title: 'Description',
                            onClick: (trade) => toDetail(trade.id),
                        },
                        {
                            key: 'actions',
                            title: '',
                            render: (trade) => (
                                <TrashCan
                                    className="cursor-pointer flex"
                                    id={trade.id}
                                    onClick={() => deleteTrade(trade.id)}
                                />
                            ),
                        },
                    ]}
                />
                {trades.data && trades.data.totalCount > PAGE_SIZE && (
                    <Pager
                        onNextPage={handleNextPage}
                        onPreviousPage={handlePreviousPage}
                        skip={skip}
                        pageSize={PAGE_SIZE}
                        objectList={trades.data}
                    />
                )}
            </div>
        </div>
    );
}
